import React, { useContext, useEffect, useRef } from 'react';
import './AddPhotos.scss';
import Button from '../../shared/Button';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import { useTranslation } from 'react-i18next';
import uniqid from 'uniqid';
import { MainFeedbackContext } from '../LocationFeedback';
import usePhotoUploader from '../../../util/usePhotoUploader';
import { Message, Dimmer, Loader } from 'semantic-ui-react';
import { getConfigData } from '../../../middleware';
import {v5 as uuid} from 'uuid';

const AddPhotos = ({
  dataKey,
  isFeedback,
  isEdit,
  handleCta,
  nextStep,
  isBI,
  nextStepLabel,
}) => {
  const {review, setReview} = useContext(MainFeedbackContext);

  const {t} = useTranslation();
  const tr = t('photos', {returnObjects: true});
  const trCommon = t('common', {returnObjects: true});

  const continueCTA = isBI()
    ? tr.buttons.next_bi
    : nextStepLabel || tr.buttons.next;

  const fieldRef = useRef(null);
  const inputOpenFileRef = useRef(null);
  const NS = getConfigData()['UUID_NS'];

  // Generate a unique key for this session, appending to dataKey (agentId) if present
  const dataKeyPlainRefName = dataKey ? `${dataKey}_${uniqid()}` : uniqid();
  const dataKeyRefName = uuid(dataKeyPlainRefName, NS);
  const photosKey = useRef(dataKeyRefName).current;

  const {
    photos,
    uploadProgress,
    isUploading,
    error,
    showError,
    closeError,
    handleFiles,
    removePhoto,
    getUploadedPhotos,
  } = usePhotoUploader({initialPhotos: review.photos, photosKey});

  useEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    // Reset so picking the same file(s) again still fires onChange
    e.target.value = '';
    handleFiles(files);
  };

  const showOpenFileDlg = () => {
    closeError();
    if (inputOpenFileRef.current) inputOpenFileRef.current.click();
  };

  const saveAndContinue = () => {
    if (isUploading) return;
    setReview({
      type: 'SAVE_PHOTOS',
      data: {
        photos: getUploadedPhotos(),
        key: photosKey,
      },
    });

    if (typeof handleCta === 'function') {
      handleCta();
    }
    if (typeof nextStep === 'function') {
      nextStep();
    }
  };

  const pristineCount = photos.filter((photo) => photo.pristine).length;

  return (
    <div className="add-photos" ref={fieldRef}>
      {isUploading && (
        <Dimmer active inverted>
          <Loader size="large">Uploading photos...</Loader>
        </Dimmer>
      )}

      {isFeedback && <p className="instructions">{tr.subtitle}</p>}

      {isFeedback && (
        <div>
          <Button
            cta={tr.buttons.camera}
            button_type="secondary"
            handleClick={showOpenFileDlg}
            isDisabled={pristineCount === 0}
          />
          <input
            ref={inputOpenFileRef}
            onChange={handleFileChange}
            type="file"
            style={{ display: 'none' }}
            multiple
            accept="image/jpeg, image/png"
            disabled={isUploading || pristineCount === 0}
          />
        </div>
      )}

      {showError && (
        <Message
          onDismiss={closeError}
          error
          header="Error"
          content={error}
        />
      )}

      <div className="grid">
        {photos.map((photo, index) => {
          const photoClassName = photo.pristine ? 'pristine' : '';
          const uploadStatus = uploadProgress[photo.uploadId];

          return (
            <div
              className="Mask"
              key={`${index}-${photo.src}`}
              onClick={photo.pristine ? showOpenFileDlg : undefined}
              style={
                photo.pristine
                  ? { cursor: pristineCount === 0 ? 'not-allowed' : 'pointer' }
                  : {}
              }
            >
              <img
                src={photo.src}
                className={photoClassName}
                alt={photo.pristine ? 'Add photo' : 'Uploaded photo'}
                style={
                  uploadStatus &&
                  uploadStatus.status !== 'complete' &&
                  !photo.pristine &&
                  !photo.isLocal
                    ? { opacity: 0.5 }
                    : {}
                }
              />

              {!photo.pristine && (
                <div className="overlay">
                  <CloseOutlinedIcon
                    className="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePhoto(index);
                    }}
                  />
                </div>
              )}

              {uploadStatus && (
                <div className="upload-progress">
                  <div className="progress-status">{uploadStatus.status}</div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadStatus.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isFeedback && (
        <div className="action-box">
          <Button
            cta={isEdit ? trCommon.save : continueCTA}
            button_type="primary"
            isDisabled={isUploading}
            handleClick={() => {
              saveAndContinue();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AddPhotos;
